import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mlm/ui';
import { setInventoryLocation } from '../../lib/api';

/**
 * Item locator: where a product sits in one branch (aisle, then rack or shelf).
 * Free text; clearing both fields removes the location.
 */
export function LocationDialog({
  branchId,
  product,
  onOpenChange,
}: {
  branchId: string;
  /** The product to place; null closes the dialog. */
  product: {
    productId: string;
    name: string;
    locationAisle: string | null;
    locationRack: string | null;
  } | null;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [aisle, setAisle] = useState('');
  const [rack, setRack] = useState('');

  useEffect(() => {
    if (!product) return;
    setAisle(product.locationAisle ?? '');
    setRack(product.locationRack ?? '');
  }, [product]);

  const save = useMutation({
    mutationFn: () =>
      setInventoryLocation(branchId, product!.productId, {
        aisle: aisle.trim() || null,
        rack: rack.trim() || null,
      }),
    meta: { successMessage: 'Location saved.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-inventory', branchId] });
      qc.invalidateQueries({ queryKey: ['admin-stock-lots'] });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={!!product} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Location</DialogTitle>
          <DialogDescription>
            {product ? `Where ${product.name} is kept in this branch.` : ''}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="space-y-1">
            <label htmlFor="location-aisle" className="text-xs text-muted-foreground">
              Aisle or area
            </label>
            <Input
              id="location-aisle"
              value={aisle}
              onChange={(e) => setAisle(e.target.value)}
              maxLength={40}
              placeholder="e.g. Aisle 3, Chiller, Back room"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="location-rack" className="text-xs text-muted-foreground">
              Rack or shelf
            </label>
            <Input
              id="location-rack"
              value={rack}
              onChange={(e) => setRack(e.target.value)}
              maxLength={40}
              placeholder="e.g. Rack B, Top shelf"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
